import { TranslatableError } from '@/i18n/errors';
import type { MessageKey } from '@/i18n/types';
import { completeUpload, requestUploadUrl, type Photo } from '@/lib/api';
import type { EncodedPhoto, EyePair } from '@/lib/encode';

/**
 * Headers the browser insists on owning.
 *
 * The backend signs only `Host`, and fetch silently drops forbidden header
 * names — replaying them verbatim would be noise at best. Content-Type is not
 * covered by the signature, so the real type can be sent freely.
 */
const FORBIDDEN_HEADERS = new Set(['host', 'content-length', 'connection']);

function uploadHeaders(signed: Record<string, string>, mime: string): HeadersInit {
  const headers: Record<string, string> = { 'Content-Type': mime };

  for (const [name, value] of Object.entries(signed)) {
    if (!FORBIDDEN_HEADERS.has(name.toLowerCase())) {
      headers[name] = value;
    }
  }

  return headers;
}

export class UploadError extends TranslatableError {
  constructor(
    key: MessageKey,
    readonly status?: number,
  ) {
    super(key);
    this.name = 'UploadError';
  }
}

/**
 * Reserves today's slot, PUTs the bytes straight to S3, then tells the API the
 * object landed. Three round trips, none of which carry the image through the
 * backend.
 */
export async function uploadPhoto(
  encoded: EncodedPhoto,
  options: { fromGallery?: boolean; eyes?: EyePair | null; score?: number } = {},
): Promise<Photo> {
  const ticket = await requestUploadUrl({
    mime: encoded.mime,
    bytes: encoded.blob.size,
    width: encoded.width,
    height: encoded.height,
    checksum: encoded.checksum,
    from_gallery: options.fromGallery ?? false,
    // Only sent together: a transform without the eyes it came from cannot be
    // recomputed later, and the API rejects a partial pair.
    ...(encoded.transform && options.eyes
      ? {
          align_transform: {
            matrix: [...encoded.transform.matrix],
            width: encoded.transform.width,
            height: encoded.transform.height,
          },
          landmarks: options.eyes,
          align_score: options.score ?? 0,
        }
      : {}),
  });

  const response = await fetch(ticket.upload.url, {
    method: 'PUT',
    // Cross-origin to the S3 host, and the signature is the only credential
    // involved: sending cookies here would be both useless and leaky.
    credentials: 'omit',
    headers: uploadHeaders(ticket.upload.headers, encoded.mime),
    body: encoded.blob,
  });

  if (!response.ok) {
    throw new UploadError('errors.upload_failed', response.status);
  }

  const { photo } = await completeUpload(ticket.photo.id);

  return photo;
}
